import { Layout, Formtitle, Fifpl, Dpc, PersoFin, Cpf, Other, Opco, GoCardless } from '../components'
import { Grid, Button, Typography } from '@mui/material'
import { FormType, FunderType } from '../utils/sharedTypes'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { client } from '../utils/client'
import Bugsnag from '@bugsnag/js'
import { NotFundable } from '../components'
import { funders } from '../utils/constants'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

type FinancementPropsType = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

const Financement = ({ form, setForm }: FinancementPropsType) => {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const personalFunding = form.data.personal_funding ?? form.data.default_personal_funding
  const hasCustomProcessToValidate =
    form.data.custom_processes_validation && form.data.pre_registration_custom_processes.some((p) => !p.validated)

  useEffect(() => {
    // PreConfiguredRegistration & ExternalRegistration: redirect "reste" (not DPC/FIFPL) to Questionnaire or next step
    // Ne pas rediriger lors d'un retour navigateur pour ne pas bloquer le bouton back
    if (navigationType === 'POP') return
    if (form.data.registration_type !== 'SelfRegistration') {
      const code = form.data.funding_organism?.code || ''
      const isReste = ['ALMA', 'STRIPE', 'BANK_TRANSFER', 'CPF'].includes(code)
      if (isReste) {
        if (form.data.positioning_status?.blocking) {
          navigate({ pathname: '../positionnement', search: location.search })
        } else if (hasCustomProcessToValidate) {
          navigate({ pathname: '../questionnaire', search: location.search })
        } else if (code === 'CPF') {
          navigate({ pathname: '../cpf', search: location.search })
        } else {
          navigate({ pathname: '../recapitulatif', search: location.search })
        }
      }
    }
  }, [
    form.data.registration_type,
    form.data.funding_organism,
    hasCustomProcessToValidate,
    form.data.positioning_status,
    location.search,
    navigate,
    navigationType,
  ])

  const handleChange = (key: string, value: string | null) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]
      return {
        ...prevState,
        data: {
          ...prevState.data,
          funding_organism: {
            ...prevState.data.funding_organism,
            [key]: value,
          } as { code: string | null; type: string | null },
        },
        errors: errors,
      }
    })
  }

  useEffect(() => {
    const productCode = form.data.product?.product_code || form.data.products[0]?.product_code
    if (productCode) {
      setIsLoading(true)
      let url = `/registration/content/funding_organisms/?product_code=${productCode}`
      if (form.professions.length !== 0 && form.professions[0]?.code !== 'unknown') {
        url += `&profession=${form?.data?.profession?.code}`
      }

      client
        .get(url)
        .then((resp) => {
          const data = resp.data as {
            results: FunderType[]
          }
          let code = null

          const sortedFunders = data.results
            .filter((f) => {
              return funders.find((p) => p.code === f.code)
            })
            .sort((a, b) => {
              const nameA = funders.find((f) => f.code === a.code)?.label
              const nameB = funders.find((f) => f.code === b.code)?.label
              if (nameA && nameB) {
                if (nameA < nameB) return -1
                if (nameA > nameB) return 1
              }
              return 0
            })
          setForm((prevState) => {
            return {
              ...prevState,
              funders: [
                ...sortedFunders,
                {
                  code: null,
                  name: 'Étude personnalisée',
                } as FunderType,
              ],
            }
          })

          if (data.results.length >= 1) code = sortedFunders[0].code

          handleChange('code', form.data.funding_organism?.code ?? code)
          handleChange('type', form.data.funding_organism?.type ?? '')
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [form.data.product, form.data.products, form?.data?.profession?.code])

  useEffect(() => {
    if (form.data.funding_organism && form.data.global_id) {
      client
        .patch(`/registration/${form.data.global_id}/`, {
          funding_organism: form.data.funding_organism,
        })
        .then((resp) => {
          console.log('resp', resp)
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }
  }, [form.data.funding_organism?.code])

  return (
    <Layout form={form.data} steps={form.steps}>
      <Formtitle
        title="Financement"
        subtitle={
          ['PreConfiguredRegistration', 'ExternalRegistration'].includes(form.data.registration_type || '')
            ? 'Veuillez confirmer les informations ci-dessous'
            : form.data.funding_organism?.code === null
              ? "Vous avez besoin d'aide & préférez être accompagné(e) par un conseiller ?"
              : 'Remplissez le formulaire ci-dessous pour poursuivre votre inscription'
        }
      />
      {['PreConfiguredRegistration', 'ExternalRegistration'].includes(form.data.registration_type || '') ? (
        form.data.funding_organism?.type !== 'opco' ? (
          <Grid item container spacing={1} sx={{ justifyContent: 'flex-start', mb: 4 }}>
            <Grid item>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  height: 56,
                }}
              >
                {['FIFPL', 'FAFPM'].includes(form.data.funding_organism?.code ?? '') && Boolean(personalFunding)
                  ? `Mixte (${funders.find((p) => p.code === form.data.funding_organism?.code)?.label || ''
                  }, personnel)`
                  : funders.find((p) => p.code === form.data.funding_organism?.code)?.label || '-'}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 4 }} align="justify">
              Selon les informations que vous nous avez fournies, le financement de cette formation sera réalisé{' '}
              {(personalFunding || 0) > 0 ? 'partiellement' : 'intégralement'} par l'OPCO{' '}
              {form.data.funding_organism?.code?.replace('_', ' ')}. Ce financement est dépendant du dépôt de votre
              demande de financement auprès de l'OPCO et de son acceptation par ce dernier.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }} align="justify">
              Retrouvez ci-dessous les documents <u>à signer et déposer ci-dessous</u> qui vous seront nécessaires pour
              déposer votre demande de financement auprès de l'OPCO.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }} align="justify">
              A la suite du dépôt de votre demande de financement, l'OPCO vous fournira un numéro de dossier. Pour
              accélérer le traitement de votre inscription{' '}
              {form.data.convention_starting_date &&
                `et s'assurer du bon début de votre formation le ${new Date(
                  form.data.convention_starting_date,
                ).toLocaleString('fr-FR', {
                  day: 'numeric',
                  month: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                })},`}{' '}
              nous vous demandons de nous transmettre ce numéro de dossier dans le formulaire ci-dessous.
            </Typography>
          </>
        )
      ) : (
        form.funders.length > 1 && (
          <Grid item container spacing={1} sx={{ justifyContent: 'flex-start', mb: 4 }}>
            {form.funders.map((f) => {
              return (
                <Grid item key={f.code}>
                  <Button
                    variant={form.data.funding_organism?.code === f.code ? 'contained' : 'outlined'}
                    fullWidth
                    onClick={() => {
                      handleChange('code', f.code)
                    }}
                    sx={{
                      height: 56,
                    }}
                  >
                    {funders.find((p) => p.code === f.code)?.label || '-'}
                  </Button>
                </Grid>
              )
            })}
          </Grid>
        )
      )}

      {!isLoading && (
        <Grid item>
          {['FIFPL', 'FAFPM'].includes(form.data.funding_organism?.code ?? '') && (
            <Fifpl form={form} setForm={setForm} />
          )}
          {form.data.funding_organism?.code === 'ALMA' && <PersoFin form={form} setForm={setForm} />}
          {form.data.funding_organism?.code === 'DPC' && <Dpc form={form} setForm={setForm} />}
          {form.data.funding_organism?.code === 'CPF' && <Cpf form={form} setForm={setForm} />}
          {form.data.funding_organism?.code === 'GOCARDLESS' && <GoCardless form={form} setForm={setForm} />}
          {form.data.funding_organism?.type === 'opco' && <Opco form={form} setForm={setForm} />}
          {form.data.funding_organism?.code === null && <Other form={form} setForm={setForm} />}
          {form.funders.length === 0 &&
            !personalFunding &&
            ['FIFPL', 'FAFPM'].includes(form.data.funding_organism?.code ?? '') && (
              <NotFundable form={form} setForm={setForm} />
            )}
        </Grid>
      )}
    </Layout>
  )
}

export default Financement
